import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, ShieldCheck, Users, Handshake, MapPin, Check } from 'lucide-react';
import Layout from '../components/Layout';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/utils';

const team = [
  {
    name: 'Christian Stobberup',
    role: 'Ejer og sælger',
    phone: '26796139',
    email: 'cs@cmskruefundament.dk',
    image: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/31e344f1-f745-4fbf-9967-a70497e0f302.jpeg',
    imageAlt: 'Christian Stobberup – Ejer og rådgiver i skruefundering hos CM Skruefundament ApS',
    imageCaption: 'Christian Stobberup, Ejer & Salg',
    imageClass: 'object-cover object-top',
    description: 'Christian står for den daglige kontakt med vores kunder, tilbudsgivning og rådgivning om de bedste funderingsløsninger til dit projekt.'
  },
  {
    name: 'Maj-Brit Kipp Jakobsen',
    role: 'Ejer og bogholderi',
    phone: '22626186',
    email: 'invoice@cmskruefundament.dk',
    image: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/maj-brit-kipp.png',
    imageAlt: 'Maj-Brit Kipp Jakobsen – Ejer og administration hos CM Skruefundament ApS',
    imageCaption: 'Maj-Brit Kipp Jakobsen, Ejer & Administration',
    imageClass: 'object-cover object-center',
    description: 'Maj-Brit styrer økonomien, bogholderiet og administrationen, og sikrer at alle processer bag kulisserne kører professionelt og effektivt.'
  }
];

const partners = [
  {
    name: 'WSF Schroeffundering i Holland',
    role: 'Leverandør',
    logo: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/420efba5-d257-4879-9811-77fbed433154.webp',
    alt: 'WSF Schroeffundering Holland – Leverandør af varmgalvaniserede jordskruer og skruefundamenter',
    description: 'Førende leverandør af skruefundamenter - kort leveringstid.',
    website: 'https://schroeffundering.nl/'
  },
  {
    name: 'Pilar ApS',
    role: 'Test & Dokumentation',
    logo: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/19de61d7-e8e8-456b-b0f6-0c1d95aad745.png',
    alt: 'Pilar ApS – Uafhængig test, dokumentation og eftervisning af bæreevne for skruefundamenter',
    description: 'Det nyeste indenfor test, dokumentation og eftervisning af bæreevne. Nødvendig dokumentation uanset konstruktionsklasse.',
    website: 'https://pilar.dk/'
  },
  {
    name: 'Kjellerup En3prenøren 2025 ApS',
    role: 'Montering',
    logo: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/a1053cd9-3f13-4fae-bf0a-938a8cc2d0d9.svg',
    alt: 'Kjellerup En3prenøren 2025 ApS – Professionel montør af skruefundamenter',
    description: 'Montering af skruefundamenter. Fleksibel og mange års erfaring.',
    website: 'https://kjellerup-en3prenøren.dk/'
  }
];

export default function About() {
  // Struktureret data for Om os (AboutPage)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Om CM Skruefundament ApS",
    "description": "Mød holdet bag CM Skruefundament ApS. Christian og Maj-Brit står klar til at hjælpe dig med professionel fundering.",
    "mainEntity": {
      "@type": "Organization",
      "name": "CM Skruefundament ApS",
      "url": "https://cmskruefundament.dk",
      "logo": "https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/34a78654-9cb6-4b5b-93fb-23a3c5578977.png",
      "employee": team.map(member => ({
        "@type": "Person",
        "name": member.name,
        "jobTitle": member.role,
        "telephone": `+45${member.phone}`,
        "email": member.email
      }))
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Om os | CM Skruefundament ApS</title>
        <meta name="description" content="Mød holdet bag CM Skruefundament ApS. Christian og Maj-Brit står klar til at hjælpe dig med professionel fundering." />
        <link rel="canonical" href="https://cmskruefundament.dk/om-os" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cmskruefundament.dk/om-os" />
        <meta property="og:title" content="Om os | CM Skruefundament ApS" />
        <meta property="og:description" content="Mød holdet bag CM Skruefundament ApS. Christian og Maj-Brit står klar til at hjælpe dig med professionel fundering." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/31e344f1-f745-4fbf-9967-a70497e0f302.jpeg" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="CM Skruefundament ApS" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cmskruefundament.dk/om-os" />
        <meta name="twitter:title" content="Om os | CM Skruefundament ApS" />
        <meta name="twitter:description" content="Mød holdet bag CM Skruefundament ApS. Christian og Maj-Brit står klar til at hjælpe dig med professionel fundering." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/31e344f1-f745-4fbf-9967-a70497e0f302.jpeg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-900 py-16 md:py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            <div className="lg:col-span-7">
              <span className="text-xs font-bold uppercase tracking-[0.3em] mb-6 block text-amber-400">Hvem er vi</span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase leading-tight tracking-tight mb-6">
                Om CM Skruefundament
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-light mb-8">
                CM Skruefundament ApS er din professionelle partner inden for skruefundering i hele Danmark. 
                Vi leverer og monterer holdbare, bæredygtige og præcise fundamenter til både private og erhvervsprojekter – altid med fokus på personlig service og fuld dokumentation.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4">
                  <p className="font-bold text-white text-sm mb-1">Erfaring & Rådgivning</p>
                  <p className="text-xs text-slate-400">Mange års ekspertise i jordbundsforhold og statisk dimensionering.</p>
                </div>
                <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4">
                  <p className="font-bold text-white text-sm mb-1">Direkte Kontakt</p>
                  <p className="text-xs text-slate-400">Du taler altid direkte med fagfolk, der kender din opgave til bunds.</p>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-5" id="kontakt-form">
              <ContactForm
                variant="hero"
                defaultService="Andet / Rådgivning"
                title="Kontakt os direkte"
                subtitle="Har du spørgsmål eller et projekt på tegnebrættet? Skriv til os her, så vender vi hurtigt tilbage."
              />
            </div>

          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-6">Mød holdet</h2>
            <p className="text-slate-600 text-lg">
              Vi er et dedikeret team, der sætter en ære i at levere høj kvalitet, god service og solid dokumentation.
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-12 lg:gap-16">
            {team.map((member, i) => {
              const optimizedMemberImage = getOptimizedImageUrl(member.image, { width: 300, height: 450, fit: 'cover', quality: 85 });
              return (
                <div key={i} className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-xs hover:shadow-md transition-shadow duration-300 flex flex-col md:flex-row items-center md:items-stretch w-[260px] md:w-full md:h-[450px] mx-auto">
                  <figure className="w-[260px] h-[450px] flex-shrink-0 bg-slate-50 overflow-hidden m-0 relative">
                    <img 
                      src={optimizedMemberImage} 
                      alt={member.imageAlt || `${member.name} – ${member.role}`} 
                      className={`w-[260px] h-[450px] ${member.imageClass || 'object-cover object-center'}`}
                      loading="lazy"
                    />
                    <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-950/80 via-slate-950/40 to-transparent p-3 text-white text-[11px] text-center font-medium">
                      {member.imageCaption}
                    </figcaption>
                  </figure>
                  <div className="p-8 md:p-10 flex-grow flex flex-col justify-between h-auto md:h-full w-full">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-400 block mb-1">
                        {member.role}
                      </span>
                      <h3 className="text-2xl font-bold uppercase tracking-tight mb-4 text-slate-900">
                        {member.name}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed mb-6">
                        {member.description}
                      </p>
                    </div>
                    
                    <div className="space-y-3 pt-4 border-t border-slate-100">
                      <a 
                        href={`tel:+45${member.phone}`} 
                        className="flex items-center gap-3 text-slate-700 hover:text-slate-900 transition-colors text-sm font-medium"
                      >
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>+45 {member.phone}</span>
                      </a>
                      <a 
                        href={`mailto:${member.email}`} 
                        className="flex items-center gap-3 text-slate-700 hover:text-slate-900 transition-colors text-sm font-medium break-all"
                      >
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{member.email}</span>
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-24 bg-slate-50 border-t border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="text-xs font-bold uppercase tracking-[0.3em] mb-4 block text-slate-500">Stærke relationer</span>
            <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-6">Vores Samarbejdspartnere</h2>
            <p className="text-slate-600 text-lg">
              Vi samarbejder med branchens bedste aktører for at sikre dig den absolut højeste kvalitet, hurtig levering og fuld dokumentation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partners.map((partner, i) => {
              const optimizedLogo = getOptimizedImageUrl(partner.logo, { width: 250, quality: 85 });
              const CardContent = (
                <div>
                  <div className="h-28 flex items-center justify-center mb-6 bg-slate-50 p-2 rounded-lg border border-slate-100">
                    {partner.logo ? (
                      <figure className="m-0 flex flex-col items-center justify-center h-full w-full">
                        <img 
                          src={optimizedLogo} 
                          alt={partner.alt || `${partner.name} – Samarbejdspartner`} 
                          title={partner.name}
                          className="max-h-16 max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </figure>
                    ) : (
                      <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center rounded-lg">
                        <Handshake className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-400 block mb-2">
                    {partner.role}
                  </span>
                  <h3 className="text-xl font-bold uppercase tracking-tight mb-4 text-slate-900 group-hover:text-slate-700 transition-colors">
                    {partner.name}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {partner.description}
                  </p>
                </div>
              );

              return partner.website ? (
                <a
                  key={i}
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-slate-100 p-8 rounded-xl shadow-xs hover:shadow-md hover:border-slate-300 transition-all duration-300 flex flex-col justify-between group text-left"
                  title={`Besøg ${partner.name}`}
                >
                  {CardContent}
                </a>
              ) : (
                <div
                  key={i}
                  className="bg-white border border-slate-100 p-8 rounded-xl shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between group text-left"
                >
                  {CardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section with Form */}
      <section className="bg-slate-900 py-20 lg:py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-amber-500 mb-4 block">Kontakt os</span>
              <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-6 text-white leading-tight">
                Har du spørgsmål til vores løsninger?
              </h2>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8">
                Vi sidder klar til at rådgive dig om dit næste projekt. Send os en besked eller ring direkte til vores team for en personlig og uforpligtende snak.
              </p>

              <div className="space-y-4 mb-8">
                <div className="p-4 bg-slate-800/90 rounded-lg border border-slate-700">
                  <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-1">Christian Stobberup – Salg & Rådgivning</p>
                  <div className="flex flex-wrap gap-4 items-center">
                    <a href="tel:26796139" className="text-sm font-bold text-white hover:text-amber-400 transition-colors flex items-center gap-1.5">
                      <Phone className="w-4 h-4 text-amber-500" /> 26 79 61 39
                    </a>
                    <a href="mailto:cs@cmskruefundament.dk" className="text-sm text-slate-300 hover:text-white transition-colors flex items-center gap-1.5">
                      <Mail className="w-4 h-4 text-amber-500" /> cs@cmskruefundament.dk
                    </a>
                  </div>
                </div>

                <div className="p-4 bg-slate-800/90 rounded-lg border border-slate-700">
                  <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-1">Maj-Brit Kipp Jakobsen – Administration</p>
                  <div className="flex flex-wrap gap-4 items-center">
                    <a href="tel:22626186" className="text-sm font-bold text-white hover:text-amber-400 transition-colors flex items-center gap-1.5">
                      <Phone className="w-4 h-4 text-amber-500" /> 22 62 61 86
                    </a>
                    <a href="mailto:invoice@cmskruefundament.dk" className="text-sm text-slate-300 hover:text-white transition-colors flex items-center gap-1.5">
                      <Mail className="w-4 h-4 text-amber-500" /> invoice@cmskruefundament.dk
                    </a>
                  </div>
                </div>

                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-800 text-sm text-slate-400 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <span>Gl. Fynsvej 1, 5580 Nørre Aaby &bull; CVR: 43232049</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
